import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import * as Dialog from '@radix-ui/react-dialog';
import * as Accordion from '@radix-ui/react-accordion';
import { Button } from '@radix-ui/themes';
import { Cross2Icon, ChevronDownIcon, PlayIcon } from '@radix-ui/react-icons';

// Import logo and feature images
import snapgradeLogo from '../../img/snapgrade-logo.svg';
import instantFeedbackImg from './images/instant-feedback.png';
import progressTrackingImg from './images/progress-tracking.png';
import mainImage from './images/main-image.png';
import consistentScoringImg from './images/consistent-scoring.png';
import customRubricsImg from './images/custom-rubrics.png';
import gradeAssignmentsImg from './images/grade-assignments.png';
import studentViewImg from './images/student-view.PNG';
import teacherQuestionsImg from './images/teacher-questions.PNG';


// Import API utilities and config
import { API_ENDPOINTS } from '../../config/api.config';
import { postData } from '../../utils/apiUtils';
import EmailVerificationModal from '../../components/EmailVerificationModal/EmailVerificationModal';

interface FeatureCardProps {
  image: string;
  title: string;
  description: string;
  icon: string;
  onImageClick: (imageSrc: string) => void;
}

interface PricingCardProps {
  planName: string;
  description: string;
  price: string;
  period: string;
  originalPrice?: string;
  billingNote?: string;
  savingsNote?: string;
  features: string[];
  buttonText: string;
  onButtonClick: () => void;
  isPopular?: boolean;
  isProcessing?: boolean;
  isProPlan?: boolean;
  showDiscount?: boolean;
}

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Pricing state
  const [classStudents, setClassStudents] = useState<number>(25);
  const [schoolTeachers, setSchoolTeachers] = useState<number>(5);
  const [schoolStudents, setSchoolStudents] = useState<number>(150);
  const [billingOption, setBillingOption] = useState<'monthly' | 'annual'>('monthly');
  
  // Payment processing state
  const [isProcessingPayment, setIsProcessingPayment] = useState<boolean>(false);
  
  // Video modal state
  const [showVideoModal, setShowVideoModal] = useState<boolean>(false);
  
  // Image lightbox modal state
  const [showImageModal, setShowImageModal] = useState<boolean>(false);
  const [modalImage, setModalImage] = useState<string>('');
  
  // Trial modal state
  const [showTrialModal, setShowTrialModal] = useState<boolean>(false);
  const [trialLoading, setTrialLoading] = useState<boolean>(false);
  const [trialData, setTrialData] = useState<any>(null);
  const [trialError, setTrialError] = useState<string | null>(null);
  
  // State for copy to clipboard
  const [copiedCode, setCopiedCode] = useState<boolean>(false);
  
  // Email verification modal state
  const [showEmailModal, setShowEmailModal] = useState<boolean>(false);
  const [currentPlanType, setCurrentPlanType] = useState<string>('');
  const [currentBillingOption, setCurrentBillingOption] = useState<string>('');
  
  // Mobile menu state
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false);
  
  // Carousel state
  const [currentImageIndex, setCurrentImageIndex] = useState<number>(0);
  
  // Carousel images array
  const carouselImages = [
    { src: mainImage, alt: "SnapGrade Dashboard Overview" },
    { src: instantFeedbackImg, alt: "AI-Powered Instant Feedback" },
    { src: studentViewImg, alt: "Student Work Submission View" },
    { src: teacherQuestionsImg, alt: "Teacher Questions Management" }
  ];

  // Video/modal handlers
  const handleVideoModalOpen = () => setShowVideoModal(true);
  const handleVideoModalClose = () => setShowVideoModal(false);
  const handleImageModalClose = () => setShowImageModal(false);
  
  // Handle image click to open lightbox
  const handleImageClick = (imageSrc: string) => {
    setModalImage(imageSrc);
    setShowImageModal(true);
  };

  // Handle trial creation
  const handleCreateTrial = async () => {
    setTrialLoading(true);
    setTrialError(null);
    
    try {
      const result = await postData(API_ENDPOINTS.TRIALS.CREATE, {});
      
      if (result.success) {
        setTrialData(result.data);
      } else {
        setTrialError(result.message || 'Failed to create trial account');
      }
    } catch (error) {
      setTrialError('An error occurred while creating your trial account. Please try again.');
      console.error('Trial creation error:', error);
    } finally {
      setTrialLoading(false);
    }
  };
  
  // Handle trial modal close
  const handleTrialModalClose = () => {
    setShowTrialModal(false);
    setTimeout(() => {
      setTrialData(null);
      setTrialError(null);
    }, 300);
  };
  
  // Copy trial code to clipboard
  const copyTrialCode = (code: string) => {
    navigator.clipboard.writeText(code)
      .then(() => {
        setCopiedCode(true);
        setTimeout(() => setCopiedCode(false), 2000);
      })
      .catch(err => console.error('Failed to copy code:', err));
  };

  // Price calculations
  const individualPrice = 4.99;
  const classPrice = 29.99;
  const classAnnualPrice = 349.99;
  const schoolBasePrice = 347.35;
  const teacherAdditionalPrice = (schoolTeachers - 5) * 30;
  const studentAdditionalPrice = (Math.floor((schoolStudents - 150) / 50)) * 55;
  const schoolPrice = schoolBasePrice + teacherAdditionalPrice + studentAdditionalPrice;

  // Payment handlers
  const handleBuyPlan = async () => {
    setCurrentPlanType('personal');
    setCurrentBillingOption(billingOption);
    setShowEmailModal(true);
  };

  const handleBuyClassroomPlan = async () => {
    setCurrentPlanType('classroom');
    setCurrentBillingOption(billingOption);
    setShowEmailModal(true);
  };
  
  const handleFreeTrial = async () => {
    setCurrentPlanType('trial');
    setCurrentBillingOption('free');
    setShowEmailModal(true);
  };
  
  const handleEmailVerified = async (email: string, planType: string, billingOption: string) => {
    try {
      setIsProcessingPayment(true);
      setShowEmailModal(false);
      
      if (planType === 'trial') {
        try {
          const response = await postData(API_ENDPOINTS.TRIALS.CHECK_EMAIL, { email });
          
          if (response.exists) {
            window.alert(response.message || "This email address is already associated with an existing account. Please use a different email address to start a free trial.");
            setTimeout(() => {
              setShowEmailModal(true);
            }, 100);
          } else {
            const timestamp = Date.now();
            const verificationToken = btoa(`${email}:${timestamp}:${process.env.REACT_APP_EMAIL_SECRET || 'snapgrade-verify'}`);
            navigate(`/register?showTrial=true&email=${encodeURIComponent(email)}&token=${encodeURIComponent(verificationToken)}&ts=${timestamp}`);
          }
        } catch (error) {
          console.error('Error checking email for trial:', error);
          
          // Extract the specific error message from the backend response
          const errorMessage = error.response?.data?.message || 
                              error.message || 
                              'An error occurred while verifying your email. Please try again.';
          
          alert(errorMessage);
          setTimeout(() => {
            setShowEmailModal(true);
          }, 100);
        } finally {
          setIsProcessingPayment(false);
        }
        return;
      }
      
      const planData = {
        planType: planType,
        billingOption: billingOption,
        customer_email: email,
        allowTrialUpgrade: true
      };
      
      const response = await postData(API_ENDPOINTS.PAYMENT.CREATE_CHECKOUT, planData);
      
      if (response && response.success && response.checkoutUrl) {
        window.location.href = response.checkoutUrl;
      } else {
        throw new Error(response?.message || 'Failed to create checkout session');
      }
      
    } catch (error) {
      console.error('Payment process error:', error);
      setIsProcessingPayment(false);
      
      const errorMessage = (error as any).response?.data?.message || (error as Error).message;
      if (errorMessage && errorMessage.toLowerCase().includes('email already exists')) {
        window.alert("This email address is already associated with an existing plan. Please use a different email address to start a new subscription.");
        setTimeout(() => {
          setShowEmailModal(true);
        }, 100);
      } else {
        alert('An error occurred during the payment process. Please try again.');
      }
    } finally {
      setIsProcessingPayment(false);
    }
  };

  // Carousel effect - cycle through images every 5 seconds
  useEffect(() => {
    const carouselInterval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => 
        (prevIndex + 1) % carouselImages.length
      );
    }, 5000); // Change image every 5 seconds

    return () => clearInterval(carouselInterval);
  }, [carouselImages.length]);

  // Check effects
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const paymentStatus = params.get('payment_status');
    
    if (paymentStatus === 'cancelled') {
      alert('Your payment process was cancelled and you have not been charged.');
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, [location]);
  
  useEffect(() => {
    const userData = localStorage.getItem('user');
    const user = userData ? JSON.parse(userData) : null;
    const isAuthenticated = !!user;
    
    if (isAuthenticated) {
      const role = user?.role?.toLowerCase() || localStorage.getItem("role")?.toLowerCase();
      
      if (role === 'owner') {
        navigate('/owner-dashboard');
      } else if (role === 'admin') {
        navigate('/admin-dashboard');
      } else {
        navigate('/dashboard');
      }
    }
  }, [navigate]);

  // Navigation Component
  const Navigation: React.FC = () => (
    <nav className="bg-[#F0D143] border-b-0 py-6 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img 
              src={snapgradeLogo} 
              alt="SnapGrade" 
              className="h-32 w-auto"
            />
          </Link>
          
          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            <a href="#product" className="text-black font-medium text-lg hover:text-gray-700 transition-colors">
              Product
            </a>
            <a href="#pricing" className="text-black font-medium text-lg hover:text-gray-700 transition-colors">
              Pricing
            </a>
            <a href="#faqs" className="text-black font-medium text-lg hover:text-gray-700 transition-colors">
              FAQs
            </a>
          </div>
          
          {/* Action Buttons */}
          <div className="hidden lg:flex items-center space-x-4">
            <Button 
              className="bg-green-500 text-white hover:bg-green-600 px-6 py-2 rounded-lg font-medium text-base transition-all duration-200"
              onClick={handleFreeTrial}
            >
              Try for free
            </Button>
            <Button 
              className="bg-black text-white hover:bg-gray-800 px-8 py-3 rounded-lg font-semibold text-base shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 border-2 border-black hover:border-gray-800"
              onClick={() => navigate('/login')}
            >
              Login
            </Button>
            <Button 
              className="bg-white text-black hover:bg-gray-100 px-8 py-3 rounded-lg font-semibold text-base border-2 border-black hover:border-gray-700 shadow-md hover:shadow-lg transition-all duration-200"
              onClick={() => window.open('http://calendly.com/snapgrade', '_blank')}
            >
              Book A Demo
            </Button>
          </div>
          
          {/* Mobile Menu Button */}
          <div className="lg:hidden">
            <Button 
              variant="ghost" 
              className="text-black hover:bg-black/10 transition-colors duration-200"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              <div className="relative w-6 h-6">
                <Cross2Icon 
                  className={`absolute inset-0 h-6 w-6 transition-all duration-300 ${
                    isMobileMenuOpen ? 'opacity-100 rotate-0' : 'opacity-0 rotate-90'
                  }`} 
                />
                <svg 
                  className={`absolute inset-0 h-6 w-6 transition-all duration-300 ${
                    isMobileMenuOpen ? 'opacity-0 -rotate-90' : 'opacity-100 rotate-0'
                  }`} 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </div>
            </Button>
          </div>
        </div>
        
        {/* Mobile Navigation */}
        <div 
          className={`lg:hidden overflow-hidden transition-all duration-300 ease-in-out ${
            isMobileMenuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="mt-4 space-y-2">
            <div className="flex flex-col space-y-2">
              <a 
                href="#product" 
                className="text-black font-medium py-2 transform transition-all duration-200 hover:translate-x-1"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Product
              </a>
              <a 
                href="#pricing" 
                className="text-black font-medium py-2 transform transition-all duration-200 hover:translate-x-1"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Pricing
              </a>
              <a 
                href="#faqs" 
                className="text-black font-medium py-2 transform transition-all duration-200 hover:translate-x-1"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                FAQs
              </a>
            </div>
            <div className="flex flex-col space-y-2 pt-2 border-t border-black/20">
              <Button 
                className="bg-green-500 text-white hover:bg-green-600 w-full py-3 rounded-lg font-semibold transition-all duration-200"
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  handleFreeTrial();
                }}
              >
                Try for free
              </Button>
              <Button 
                className="bg-black text-white hover:bg-gray-800 w-full py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 border-2 border-black hover:border-gray-800"
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  navigate('/login');
                }}
              >
                Login
              </Button>
              <Button 
                className="bg-white text-black hover:bg-gray-100 w-full py-3 rounded-lg font-semibold border-2 border-black hover:border-gray-700 shadow-md hover:shadow-lg transition-all duration-200"
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  window.open('http://calendly.com/snapgrade', '_blank');
                }}
              >
                Book A Demo
              </Button>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );

  // Hero Section Component
  const HeroSection: React.FC = () => (
    <section className="bg-[#F0D143] py-16 lg:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="text-center lg:text-left">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-black leading-tight mb-6">
              Grade Smarter,
              <br />
              <span className="font-extrabold">Not Harder</span>
            </h1>
            <p className="text-lg lg:text-xl text-black/80 mb-8 max-w-lg mx-auto lg:mx-0">
              AI-powered grading that saves teachers 10+ hours weekly while providing detailed, 
              personalized feedback to every student
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                className="bg-green-500 text-white hover:bg-green-600 px-8 py-3 rounded-lg font-semibold text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                onClick={handleFreeTrial}
              >
                Get Free Plan
              </Button>
              <Button 
                className="bg-red-600 text-white hover:bg-red-700 px-8 py-3 rounded-lg font-medium text-lg flex items-center gap-2 transition-colors"
                onClick={handleVideoModalOpen}
              >
                <PlayIcon className="w-5 h-5" />
                Watch Demo
              </Button>
            </div>
          </div>
          
          {/* Right Content - Product Preview Carousel */}
          <div className="relative">
            <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-8 border border-white/30">
              <div className="bg-white rounded-xl overflow-hidden shadow-lg relative">
                {/* Carousel Container */}
                <div className="relative w-full h-auto overflow-hidden">
                  {carouselImages.map((image, index) => (
                    <div
                      key={index}
                      className={`absolute inset-0 transition-all duration-700 ease-in-out transform ${
                        index === currentImageIndex 
                          ? 'opacity-100 scale-100' 
                          : 'opacity-0 scale-105'
                      }`}
                      style={{
                        zIndex: index === currentImageIndex ? 2 : 1
                      }}
                    >
                      <img 
                        src={image.src}
                        alt={image.alt}
                        className="w-full h-auto object-cover"
                      />
                    </div>
                  ))}
                  {/* Base image for sizing (hidden but maintains layout) */}
                  <img 
                    src={carouselImages[0].src}
                    alt="Layout reference"
                    className="w-full h-auto invisible"
                  />
                </div>
                
                {/* Carousel Indicators */}
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
                  {carouselImages.map((_, index) => (
                    <button
                      key={index}
                      className={`w-3 h-3 rounded-full transition-all duration-300 ${
                        index === currentImageIndex 
                          ? 'bg-[#F0D143] scale-110' 
                          : 'bg-white/50 hover:bg-white/70'
                      }`}
                      onClick={() => setCurrentImageIndex(index)}
                      aria-label={`View ${carouselImages[index].alt}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );

  // Stats Section Component
  const StatsSection: React.FC = () => (
    <section className="bg-[#F0D143] py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-bold text-black mb-4">
            Save your teachers hours of time
          </h2>
        </div>
        
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 max-w-5xl mx-auto">
          <div className="text-center">
            <div className="bg-[#FCF6DF] rounded-2xl p-6 h-32 flex flex-col justify-center hover:bg-[#EFE8C9] transition-colors shadow-lg">
              <div className="text-3xl lg:text-4xl font-bold text-black mb-2">20</div>
              <div className="text-sm font-semibold text-black mb-1">hours</div>
              <div className="text-xs text-gray-600">Saved per week</div>
            </div>
          </div>
          
          <div className="text-center">
            <div className="bg-[#FCF6DF] rounded-2xl p-6 h-32 flex flex-col justify-center hover:bg-[#EFE8C9] transition-colors shadow-lg">
              <div className="text-2xl lg:text-3xl font-bold text-black mb-2">ALL</div>
              <div className="text-sm font-semibold text-black">SUBJECTS</div>
            </div>
          </div>
          
          <div className="text-center">
            <div className="bg-[#FCF6DF] rounded-2xl p-6 h-32 flex flex-col justify-center hover:bg-[#EFE8C9] transition-colors shadow-lg">
              <div className="text-3xl lg:text-4xl font-bold text-black mb-2">95%</div>
              <div className="text-sm font-semibold text-black mb-1">Time Saved</div>
              <div className="text-xs text-gray-600">Grading</div>
            </div>
          </div>
          
          <div className="text-center">
            <div className="bg-[#FCF6DF] rounded-2xl p-6 h-32 flex flex-col justify-center hover:bg-[#EFE8C9] transition-colors shadow-lg">
              <div className="text-3xl lg:text-4xl font-bold text-black mb-2">5-19</div>
              <div className="text-sm font-semibold text-black mb-1">Years old</div>
              <div className="text-xs text-gray-600">Suitable for all students</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );

  // Feature Card Component
  const FeatureCard: React.FC<FeatureCardProps> = ({ image, title, description, icon, onImageClick }) => (
    <div className="bg-[#FCF6DF] rounded-2xl overflow-hidden shadow-lg hover:shadow-xl hover:bg-[#EFE8C9] hover:-translate-y-1 transition-all duration-300">
      <div className="h-48 overflow-hidden cursor-pointer" onClick={() => onImageClick(image)}>
        <img 
          src={image} 
          alt={title}
          className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
        />
      </div>
      <div className="p-6">
        <div className="flex items-start justify-between mb-3">
          <h3 className="text-xl font-semibold text-black flex-1">{title}</h3>
          <div className="bg-black/5 rounded-full p-2 ml-2 flex-shrink-0">
            <span className="text-lg">{icon}</span>
          </div>
        </div>
        <p className="text-gray-600">{description}</p>
      </div>
    </div>
  );

  // Features Section Component
  const FeaturesSection: React.FC = () => (
    <section className="bg-[#F0D143] py-16 lg:py-24" id="product">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-5xl font-bold text-black mb-4">
            Everything You Need to Transform Grading
          </h2>
          <p className="text-lg text-black/70">Powerful features designed by teachers, for teachers</p>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          <FeatureCard
            image={gradeAssignmentsImg}
            title="AI-Powered Grading"
            description="Advanced algorithms assess essays, short answers, and even handwritten work with accuracy matching human graders"
            icon="🤖"
            onImageClick={handleImageClick}
          />
          <FeatureCard
            image={customRubricsImg}
            title="Custom Rubrics"
            description="Create and apply your own grading criteria or use our library of standards-aligned rubrics for consistent assessment"
            icon="📋"
            onImageClick={handleImageClick}
          />
          <FeatureCard
            image={instantFeedbackImg}
            title="Personalized Feedback"
            description="Generate detailed, constructive feedback for each student that identifies strengths and areas for improvement"
            icon="💬"
            onImageClick={handleImageClick}
          />
        </div>
        

      </div>
    </section>
  );

  // Pricing Card Component
  const PricingCard: React.FC<PricingCardProps> = ({ 
    planName, 
    description, 
    price, 
    period, 
    originalPrice, 
    billingNote, 
    savingsNote, 
    features, 
    buttonText, 
    onButtonClick, 
    isPopular, 
    isProcessing, 
    isProPlan,
    showDiscount 
  }) => (
    <div className={`relative ${isPopular ? 'p-1 bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl' : ''}`}>
      {isPopular && (
        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-blue-600 text-white text-xs font-bold px-4 py-1 rounded-full border-2 border-white z-20">
          MOST POPULAR
        </div>
      )}
      <div className={`relative bg-[#FCF6DF] rounded-2xl border ${isPopular ? 'border-0' : 'border-black/10'} overflow-hidden hover:bg-[#EFE8C9] hover:-translate-y-1 transition-all duration-300 h-full flex flex-col`}>
        {showDiscount && (
          <div className="absolute top-3 right-3 bg-orange-500 text-white text-xs font-bold px-2 py-1 rounded z-10">
            15% OFF
          </div>
        )}
        
        <div className="p-6 border-b border-black/10">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xl font-semibold text-black">{planName}</h3>
          </div>
        <p className="text-gray-600 text-sm mb-4">{description}</p>
        
        <div className={`${isProPlan ? 'text-center' : 'text-left'}`}>
          {originalPrice && (
            <div className="text-gray-500 line-through text-sm mb-1">{originalPrice}</div>
          )}
          {isProPlan ? (
            <div className="flex flex-col items-center justify-center mb-2">
              <span className="text-3xl font-bold text-black mb-1">{price}</span>
              <span className="text-lg text-gray-600">{period}</span>
            </div>
          ) : price === 'FREE' ? (
            <div className={`flex items-baseline justify-start mb-2`}>
              <span className="text-4xl font-bold text-green-600">{price}</span>
              <span className="text-gray-600 ml-2">{period}</span>
            </div>
          ) : (
            <div className={`flex items-baseline justify-start mb-2`}>
              <span className="text-2xl font-bold text-black">£</span>
              <span className="text-4xl font-bold text-black">{price.split('.')[0]}</span>
              <span className="text-2xl font-bold text-black">.{price.split('.')[1] || '00'}</span>
              <span className="text-gray-600 ml-1">{period}</span>
            </div>
          )}
          {billingNote && (
            <div className="text-gray-600 text-sm">{billingNote}</div>
          )}
          {savingsNote && (
            <div className="bg-green-50 text-green-700 text-xs font-medium px-3 py-2 rounded mt-2">
              {savingsNote}
            </div>
          )}
        </div>
      </div>
      
      <div className="p-6 flex-1 flex flex-col">
        <ul className="space-y-3 mb-6 flex-1">
          {features.map((feature, index) => (
            <li key={index} className="flex items-start text-sm">
              <span className="text-green-500 font-bold mr-2 mt-0.5">✓</span>
              <span className="text-gray-700">{feature}</span>
            </li>
          ))}
        </ul>
        
        <div className={`${isProPlan ? 'space-y-2' : ''}`}>
          <Button 
            className={`w-full bg-[#F0D143] hover:bg-[#E0C133] text-black font-semibold py-3 rounded-lg transition-colors ${isProPlan ? 'text-sm' : ''}`}
            onClick={onButtonClick}
            disabled={isProcessing}
          >
            {isProcessing ? 'Processing...' : buttonText}
          </Button>
          {isProPlan && (
            <Button 
              variant="outline"
              className="w-full border-black text-black hover:bg-black/5 font-medium py-3 rounded-lg text-sm"
              onClick={() => window.open('http://calendly.com/snapgrade', '_blank')}
            >
              Schedule a Call
            </Button>
          )}
        </div>
      </div>
    </div>
    </div>
  );

  // Pricing Section Component
  const PricingSection: React.FC = () => (
    <section className="bg-[#F0D143] py-16 lg:py-24" id="pricing">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-5xl font-bold text-black mb-4">
            Simple, Transparent Pricing
          </h2>
          
          {/* Billing Toggle */}
          <div className="flex justify-center mb-8">
            <div className="bg-black/10 rounded-full p-1 inline-flex">
              <Button
                variant={billingOption === 'monthly' ? 'solid' : 'ghost'}
                className={`px-6 py-2 rounded-full font-medium transition-colors ${
                  billingOption === 'monthly' 
                    ? 'bg-green-500 text-white' 
                    : 'text-black hover:bg-black/5'
                }`}
                onClick={() => setBillingOption('monthly')}
              >
                Monthly
              </Button>
              <Button
                variant={billingOption === 'annual' ? 'solid' : 'ghost'}
                className={`px-6 py-2 rounded-full font-medium transition-colors relative ${
                  billingOption === 'annual' 
                    ? 'bg-green-500 text-white' 
                    : 'text-black hover:bg-black/5'
                }`}
                onClick={() => setBillingOption('annual')}
              >
                Yearly
                <span className="absolute -top-2 -right-2 bg-orange-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                  15% OFF
                </span>
              </Button>
            </div>
          </div>
        </div>
        
        <div className="grid lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
          {/* Free Trial Plan */}
          <PricingCard
            planName="Free Plan"
            description="Perfect for trying SnapGrade risk-free"
            price="FREE"
            period="forever"
            features={[
              '1 teacher account',
              'Up to 3 students',
              '2 classes maximum',
              '3 questions per class',
              '10 submissions',
              'AI-powered grading',
              'Instant feedback generation',
              'Basic analytics',
              'Email support'
            ]}
            buttonText="Get Free Plan →"
            onButtonClick={handleFreeTrial}
            isProcessing={isProcessingPayment}
            showDiscount={false}
          />
          
          {/* Personal Plan */}
          <PricingCard
            planName="Personal"
            description="Best for individual students, parents & homeschoolers"
            price={billingOption === 'monthly' ? '4.99' : '4.24'}
            period={billingOption === 'monthly' ? '/month' : '/month'}
            billingNote={billingOption === 'annual' ? 'Billed as £50.91/year' : undefined}
            savingsNote={billingOption === 'annual' ? 'Save £10 with yearly pricing (15% off)' : undefined}
            features={[
              '30 practice assessments each month',
              'Multiple attempts: 3 submissions per question with instant AI feedback',
              '24/7 access from any device',
              'No commitment - cancel anytime'
            ]}
            buttonText="Get started →"
            onButtonClick={handleBuyPlan}
            isProcessing={isProcessingPayment}
            showDiscount={billingOption === 'annual'}
          />
          
          {/* Classroom Plan */}
          <PricingCard
            planName="Classroom"
            description="Best for growing educators"
            price={billingOption === 'monthly' ? '29.99' : '25.49'}
            period={billingOption === 'monthly' ? '/month' : '/month'}
            billingNote={billingOption === 'annual' ? 'Billed as £305.91/year' : undefined}
            savingsNote={billingOption === 'annual' ? 'Save £54 with yearly pricing (15% off)' : undefined}
            features={[
              'Complete solution for up to 30 students',
              '30 assessments per month for your entire class',
              'Personalized learning paths: 3 attempts per question for each student',
              'Time-saving automated grading',
              'Detailed diagnostic feedback for each student',
              'Priority support for teachers'
            ]}
            buttonText="Get started →"
            onButtonClick={handleBuyClassroomPlan}
            isPopular={true}
            isProcessing={isProcessingPayment}
            showDiscount={billingOption === 'annual'}
          />
          
          {/* Pro Plan */}
          <PricingCard
            planName="Pro"
            description="Best for scaling institutions"
            price="Contact us for"
            period="custom pricing"
            features={[
              'Unlimited teachers and students',
              'Custom assessment volumes',
              'Advanced analytics and reporting',
              'Priority technical support',
              'Custom integrations',
              'Dedicated account manager',
              'Training and onboarding support'
            ]}
            buttonText="Email Us"
            onButtonClick={() => window.location.href = 'mailto:francis.jones@snapgrade.ai'}
            isProPlan={true}
          />
        </div>
      </div>
    </section>
  );

  // FAQs Section Component
  const FAQsSection: React.FC = () => (
    <section className="bg-[#F0D143] py-16 lg:py-24" id="faqs">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-5xl font-bold text-black mb-4">
            Frequently Asked Questions
          </h2>
        </div>
        
        <Accordion.Root type="single" collapsible className="space-y-4">
          <Accordion.Item value="item-1" className="bg-[#FCF6DF] rounded-2xl overflow-hidden shadow-lg hover:bg-[#EFE8C9] transition-colors">
            <Accordion.Trigger className="w-full p-6 text-left font-semibold text-black hover:bg-[#EFE8C9] transition-colors flex items-center justify-between">
              How does AI-powered grading work?
              <ChevronDownIcon className="w-5 h-5 transition-transform duration-200" />
            </Accordion.Trigger>
            <Accordion.Content className="px-6 pb-6 text-gray-600">
              SnapGrade uses state-of-the-art AI to digitalise handwritten or typed work and then marks it, the same way a human teacher would mark it, against the provided marking rubric for each question.
            </Accordion.Content>
          </Accordion.Item>
          
          <Accordion.Item value="item-2" className="bg-[#FCF6DF] rounded-2xl overflow-hidden shadow-lg hover:bg-[#EFE8C9] transition-colors">
            <Accordion.Trigger className="w-full p-6 text-left font-semibold text-black hover:bg-[#EFE8C9] transition-colors flex items-center justify-between">
              Can I integrate SnapGrade with my existing MIS?
              <ChevronDownIcon className="w-5 h-5 transition-transform duration-200" />
            </Accordion.Trigger>
            <Accordion.Content className="px-6 pb-6 text-gray-600">
              The feature to export grades and marking to existing MIS is a feature that will be coming to SnapGrade soon.
            </Accordion.Content>
          </Accordion.Item>
          
          <Accordion.Item value="item-3" className="bg-[#FCF6DF] rounded-2xl overflow-hidden shadow-lg hover:bg-[#EFE8C9] transition-colors">
            <Accordion.Trigger className="w-full p-6 text-left font-semibold text-black hover:bg-[#EFE8C9] transition-colors flex items-center justify-between">
              Is student data secure with SnapGrade?
              <ChevronDownIcon className="w-5 h-5 transition-transform duration-200" />
            </Accordion.Trigger>
            <Accordion.Content className="px-6 pb-6 text-gray-600">
              We don't save images uploaded to SnapGrade. Once work is scanned, the text is extracted and the image is deleted immediately so we can not access the images. All that is saved is the score and feedback for each student's piece of submitted work.
            </Accordion.Content>
          </Accordion.Item>
          
          <Accordion.Item value="item-4" className="bg-[#FCF6DF] rounded-2xl overflow-hidden shadow-lg hover:bg-[#EFE8C9] transition-colors">
            <Accordion.Trigger className="w-full p-6 text-left font-semibold text-black hover:bg-[#EFE8C9] transition-colors flex items-center justify-between">
              How long does it take to set up SnapGrade?
              <ChevronDownIcon className="w-5 h-5 transition-transform duration-200" />
            </Accordion.Trigger>
            <Accordion.Content className="px-6 pb-6 text-gray-600">
              You can set up SnapGrade for us across an entire class in less than 10 minutes and for an entire school in less than 30 minutes. Apart from setting questions, all schools need to do is assign a teacher to each respective class and assign the correct students to each respective class.
            </Accordion.Content>
          </Accordion.Item>
          
          <Accordion.Item value="item-5" className="bg-[#FCF6DF] rounded-2xl overflow-hidden shadow-lg hover:bg-[#EFE8C9] transition-colors">
            <Accordion.Trigger className="w-full p-6 text-left font-semibold text-black hover:bg-[#EFE8C9] transition-colors flex items-center justify-between">
              I'm having questions using SnapGrade. How do I use it?
              <ChevronDownIcon className="w-5 h-5 transition-transform duration-200" />
            </Accordion.Trigger>
            <Accordion.Content className="px-6 pb-6 text-gray-600">
              You can view our detailed full video tutorial on how to use SnapGrade's suite of features{' '}
              <a 
                href="https://youtu.be/esmiP3EwvO4" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 underline font-medium"
              >
                here
              </a>
              . Click on the timestamps in the video description to get the part you would like help with.
            </Accordion.Content>
          </Accordion.Item>

          <Accordion.Item value="item-6" className="bg-[#FCF6DF] rounded-2xl overflow-hidden shadow-lg hover:bg-[#EFE8C9] transition-colors">
            <Accordion.Trigger className="w-full p-6 text-left font-semibold text-black hover:bg-[#EFE8C9] transition-colors flex items-center justify-between">
              What's included in the free plan?
              <ChevronDownIcon className="w-5 h-5 transition-transform duration-200" />
            </Accordion.Trigger>
            <Accordion.Content className="px-6 pb-6 text-gray-600">
              Our free plan gives you permanent access to SnapGrade with 1 teacher account, up to 3 students, 2 classes, 3 questions per class, and 10 submissions. You'll get AI-powered grading, instant feedback, basic analytics, and email support. No credit card required and no expiration date.
            </Accordion.Content>
          </Accordion.Item>

        </Accordion.Root>
      </div>
    </section>
  );

  // CTA Section Component
  const CTASection: React.FC = () => (
    <section className="bg-[#F0D143] py-16 lg:py-24" id="contact">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="text-3xl lg:text-5xl font-bold text-black mb-6">
          Ready to Transform Your Grading Process?
        </h2>
        <p className="text-lg lg:text-xl text-black/80 mb-8 max-w-2xl mx-auto">
          Join educators who are saving time and providing better feedback with SnapGrade.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button 
            className="bg-green-500 text-white hover:bg-green-600 px-8 py-3 rounded-lg font-semibold text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
            onClick={handleFreeTrial}
          >
            Get Free Plan
          </Button>
          <Button 
            className="bg-black text-white hover:bg-gray-800 px-8 py-3 rounded-lg font-semibold text-lg"
            onClick={() => window.location.href = '#pricing'}
          >
            View Pricing
          </Button>
          <Button 
            className="bg-white text-black hover:bg-gray-100 px-8 py-3 rounded-lg font-semibold text-lg border-2 border-black hover:border-gray-700 shadow-md hover:shadow-lg transition-all duration-200"
            onClick={() => window.open('http://calendly.com/snapgrade', '_blank')}
          >
            Request A Demo
          </Button>
        </div>
      </div>
    </section>
  );

  // Footer Component
  const Footer: React.FC = () => (
    <footer className="bg-black text-white py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-8">
          <div className="lg:col-span-2">
            <div className="bg-[#F0D143] p-3 rounded-lg inline-block mb-4">
              <img src={snapgradeLogo} alt="SnapGrade" className="h-16 w-auto" />
            </div>
            <p className="text-gray-400 text-sm">
              © 2025 SnapGrade, from STEM@Home Ltd. All rights reserved.
            </p>
          </div>
          
          <div>
            <h5 className="font-semibold mb-4">Product</h5>
            <ul className="space-y-2 text-sm text-gray-400">
              <li><a href="#product" className="hover:text-white transition-colors">Features</a></li>
              <li><a href="#pricing" className="hover:text-white transition-colors">Pricing</a></li>
            </ul>
          </div>
          
          <div>
            <h5 className="font-semibold mb-4">Company</h5>
            <ul className="space-y-2 text-sm text-gray-400">
              <li><a href="mailto:francis.jones@snapgrade.ai" className="hover:text-white transition-colors">Contact</a></li>
            </ul>
          </div>
          
          <div>
            <h5 className="font-semibold mb-4">Legal</h5>
            <ul className="space-y-2 text-sm text-gray-400">
              <li><Link to="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</Link></li>
              <li><Link to="/eula" className="hover:text-white transition-colors">End User Licence Agreement</Link></li>
            </ul>
          </div>
        </div>
        
        <div className="border-t border-gray-800 mt-12 pt-8">
          <div className="text-center">
            <h5 className="font-semibold mb-4">Socials</h5>
            <a 
              href="https://www.linkedin.com/company/snapgradeuk/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-white transition-colors"
            >
              LinkedIn
            </a>
          </div>
        </div>
      </div>
    </footer>
  );

  return (
    <div className="min-h-screen bg-[#F0D143] overflow-x-hidden">
      <Navigation />
      <HeroSection />
      <StatsSection />
      <FeaturesSection />
      <PricingSection />
      <FAQsSection />
      <CTASection />
      <Footer />

      {/* Video Modal */}
      <Dialog.Root open={showVideoModal} onOpenChange={setShowVideoModal}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/90 z-50" />
          <Dialog.Content className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="relative w-full max-w-6xl">
              <Dialog.Close className="absolute top-4 right-4 text-white hover:text-gray-300 z-10">
                <Cross2Icon className="w-8 h-8" />
              </Dialog.Close>
              <div className="relative w-full h-0 pb-[56.25%]">
                <iframe
                  src="https://www.youtube.com/embed/wVYmyam4vFQ?autoplay=1&modestbranding=1&rel=0"
                  title="SnapGrade Demo Video"
                  className="absolute inset-0 w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* Image Lightbox Modal */}
      <Dialog.Root open={showImageModal} onOpenChange={setShowImageModal}>
        <Dialog.Portal>
          <Dialog.Overlay 
            className="fixed inset-0 bg-black/90 z-50" 
            onClick={() => setShowImageModal(false)}
          />
          <Dialog.Content className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Close button positioned on the black background */}
            <Dialog.Close className="absolute top-4 right-4 text-white hover:text-gray-300 z-20 bg-black/50 rounded-full p-2">
              <Cross2Icon className="w-6 h-6" />
            </Dialog.Close>
            <div className="relative max-w-7xl max-h-full">
              {modalImage && (
                <img 
                  src={modalImage} 
                  alt="Enlarged view" 
                  className="max-w-full max-h-full object-contain rounded-lg shadow-2xl" 
                />
              )}
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* Email Verification Modal */}
      <EmailVerificationModal
        show={showEmailModal}
        onHide={() => setShowEmailModal(false)}
        onSuccess={handleEmailVerified}
        planType={currentPlanType}
        billingOption={currentBillingOption}
      />
    </div>
  );
};

export default LandingPage; 